import React, { useState } from 'react'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { selectData } from 'layouts/onboarding'
import { toast } from 'react-toastify'
import { callZapier } from 'utils/zapier/callZapier'

import {
  Card,
  Grid,
  Typography,
  Collapse,
  Button,
  IconButton,
  Snackbar,
} from '@mui/material'
import { ContentCopy, ExpandMore } from '@mui/icons-material'
import FormField from 'components/FormField'
import { format, differenceInYears, parseISO } from 'date-fns'
import MuiCurrencyFloatField from 'components/FormField/MuiCurrencyFloatField'

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

function AnnuityApplicationCreator({
  collapseSections,
  toggleCollapse,
  valueOfInvestableAssets,
  adjustedNetTakeHomeDifferencePARCOGap,
  intakeState,
  netWorth = 0,
  assets = [],
  liabilities = [],
  updateIntakeStateAtKey,
  updateIntakeStateAndSubmit,
}) {
  const [localForm, setLocalForm] = useState({
    carrier: intakeState?.annuityApplication?.carrier || '',
    product: intakeState?.annuityApplication?.product || '',
    amount: intakeState?.annuityApplication?.amount || '',
  })
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [jsonMapping, setJsonMapping] = useState('')
  // console.log(localForm)
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }
  const onBlur = () => {
    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({
        annuityApplication: localForm,
      })
    )()
  }

  const assetSum =
    (assets || []).map((assets) => assets.amount).reduce((a, b) => a + b, 0) +
    (intakeState.tspBalance ? intakeState.tspBalance : 0) +
    (intakeState.tspRothBalance ? intakeState.tspRothBalance : 0)

  const liabilitySum = (liabilities || [])
    .map((liabilities) => liabilities.amount)
    .reduce((a, b) => a + b, 0)

  const selectStyles = {
    '&>label': {
      fontSize: '14px',
      color: '#75787B',
      top: '-2px',
    },
    '&>label.Mui-focused': {
      fontSize: '1rem',
      color: '#2370ED',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.Mui-focused.Mui-error': {
      color: '#ea0606',
    },
    '&>label.MuiFormLabel-filled, &>label.MuiInputLabel-shrink': {
      fontSize: '1rem',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.MuiFormLabel-filled.Mui-disabled': {
      top: 0,
      background:
        'linear-gradient(to bottom, #fff 0%, #fff 50%, #e9ecef 50%, #e9ecef 100%)',
    },
    '&>div.MuiInputBase-root': {
      padding: '0!important',
      borderRadius: '4px !important',
    },
    '&>div.MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#e9ecef!important',
    },
    '&>div.MuiInputBase-root.Mui-focused': {
      boxShadow: '0 0 0 1.5px #2370ED',
    },
    '&>div>.MuiInputBase-input': {
      width: '100%!important',
      padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
      display: 'flex',
    },
    '&>div>.MuiInputBase-input>.MuiListItemIcon-root': {
      minWidth: 'unset',
    },
    '&>div>.MuiInputBase-input>.MuiListItemIcon-root>img': {
      height: '20px',
      width: 'auto',
      marginRight: '8px',
    },
    '&>div>.MuiInputBase-input>.MuiListItemText-root>span': {
      fontSize: '14px',
    },
    '&>div.MuiInputBase-root>svg': {
      display: 'block',
      height: '1.5em',
      width: '1.5em',
      top: 'calc(50% - 0.75em)',
    },
  }
  const carrierLogoStyles = {
    '&>.MuiListItemIcon-root': {
      minWidth: '100px',
      justifyContent: 'center',
    },
    '&>.MuiListItemIcon-root>img': {
      width: 'auto',
      height: 20,
      marginRight: '8px',
    },
    '&>.MuiListItemText-root': {},
    '&>.MuiListItemText-root>span': { fontSize: '14px' },
  }

  /*
    TODO:
     - Update values in master_data and annuitysetup with values from the user/rep
     - Use another api client instead of fetch.
  */

  // console.log('gender:', intakeState?.gender)
  // console.log(
  //   'genderLabel:',
  //   selectData.gender.find((ac) => ac.value === intakeState?.gender)?.label ||
  //     ''
  // )

  const getApplicationData = () => {
    const myHeaders = new Headers()
    myHeaders.append('Content-Type', 'application/json')
    myHeaders.append('Access-Control-Allow-Origin', '*')
    myHeaders.append('Access-Control-Allow-Methods', 'POST')
    myHeaders.append('Access-Control-Allow-Headers', 'Content-Type')
    myHeaders.append('Access-Control-Max-Age', '3600')
    const raw = JSON.stringify({
      master_data: {
        __Num_Additional_Agents__: '1',
        __Add_Split_Rep__: 'Yes',
        __Agent_First_Name__: selectData.retirementStrategist.find(
          (ac) =>
            ac.value === (intakeState?.retirementStrategist || 'john_mantia')
        ).firstName,
        __Agent_Last_Name__: selectData.retirementStrategist.find(
          (ac) =>
            ac.value === (intakeState?.retirementStrategist || 'john_mantia')
        ).lastName,
        __Agent_Full_Name__: selectData.retirementStrategist.find(
          (ac) =>
            ac.value === (intakeState?.retirementStrategist || 'john_mantia')
        ).label,
        __Commission_Option__: 'A',
        __Commission_Option_A__: 'true',
        __Commission_Code__: '1',
        __Commission_Percentage__: '1',
        __Business_Name__: 'PARCO',
        __Agent_Phone__: selectData.retirementStrategist.find(
          (ac) =>
            ac.value === (intakeState?.retirementStrategist || 'john_mantia')
        ).phone,
        __Agent_Cell_Phone__: selectData.retirementStrategist.find(
          (ac) =>
            ac.value === (intakeState?.retirementStrategist || 'john_mantia')
        ).phone,
        __Agent_Website__: 'https://goparco.com',
        __Agent_Address__: '2902 W. Cary St.',
        __Agent_City__: 'Richmond',
        __Agent_State__: 'VA',
        __Agent_License_State__: 'Virginia',
        __Agent_Zip__: '23221',
        __Agent_Email__: selectData.retirementStrategist.find(
          (ac) =>
            ac.value === (intakeState?.retirementStrategist || 'john_mantia')
        ).email,
        __Agent_ID_Number__: selectData.retirementStrategist.find(
          (ac) =>
            ac.value === (intakeState?.retirementStrategist || 'john_mantia'),
        ).id.toString(),
        __Agent_NPN_Number__: selectData.retirementStrategist.find(
          (ac) =>
            ac.value === (intakeState?.retirementStrategist || 'john_mantia')
        ).npnNumber,
        __Additional_Commission_Option__: 'A',
        __Additional_Commission_Percentage__: '99',
        __Client_Firstname__: intakeState?.firstName || '',
        __Client_Lastname__: intakeState?.lastName || '',
        __Client_Fullname__:
          intakeState?.firstName + ' ' + intakeState?.lastName || '',
        __Client_Gender__:
          selectData.gender.find((ac) => ac.value === intakeState?.gender)
            ?.label || '',
        __Client_Gender_UpperCase__:
          selectData.gender
            .find((ac) => ac.value === intakeState?.gender)
            ?.label.toUpperCase() || '',
        __Client_DOB__: intakeState?.dateOfBirth
          ? format(parseISO(intakeState?.dateOfBirth), 'MM/dd/yyyy')
          : '',
        __Client_Age__: differenceInYears(
          new Date(),
          parseISO(intakeState?.dateOfBirth) || new Date()
        ).toString(),
        __Client_Address_1__: intakeState?.address || '',
        __Client_Address_2__: intakeState?.address2 || '',
        __Client_City__: intakeState?.city || '',
        __Client_State__: selectData.states.find(
          (ac) => ac.value === (intakeState?.state || '')
        ).label,
        __Client_State_Abbrev__: selectData.states.find(
          (ac) => ac.value === (intakeState?.state || '')
        ).abbr,
        __Client_State_Number__: '',
        __Client_Zipcode__: intakeState?.zipcode || '',
        __Client_Email__: intakeState?.email || '',
        __Client_Phone__: intakeState?.phone || '',
        __Client_Military_Yes_No__: intakeState?.military ? 'Yes' : 'No' || '',
        __Client_Military_True_False__: intakeState?.military
          ? 'true'
          : 'false' || '',
        __Client_Annual_Income__: intakeState?.baseSalary.toString() || '',
        __Client_Annual_Expenses__: intakeState?.baseSalary
          ? Math.round(intakeState.baseSalary * 0.41).toString()
          : '',
        __Client_Net_Worth__: netWorth.toString() || '',
        __Client_Value_Of_Annuities__: localForm.amount.toString() || '',
        __Client_Liquid_Net_Worth_Before__:
          intakeState?.totalInvestableAssets.toString() || '',
        __Client_Liquid_Net_Worth_After__:
          (
            intakeState?.totalInvestableAssets - localForm.amount || 0
          ).toString() || '',
        __Client_Liquid_Net_Worth_Less_30_No__:
          (intakeState?.totalInvestableAssets - localForm.amount || 0) < 30000
            ? 'false'
            : 'true',
        __Client_Liquid_Net_Worth_Less_30_Yes__:
          (intakeState?.totalInvestableAssets - localForm.amount || 0) < 30000
            ? 'true'
            : 'false',
        __Annuitant_Firstname__: intakeState?.firstName || '',
        __Annuitant_Lastname__: intakeState?.lastName || '',
        __Annuitant_Fullname__:
          intakeState?.firstName + ' ' + intakeState?.lastName || '',
        __Annuitant_Gender__:
          selectData.gender.find((ac) => ac.value === intakeState?.gender)
            ?.label || '',
        __Annuitant_Gender_UpperCase__:
          selectData.gender
            .find((ac) => ac.value === intakeState?.gender)
            ?.label.toUpperCase() || '',
        __Annuitant_DOB__: intakeState?.dateOfBirth
          ? format(parseISO(intakeState?.dateOfBirth), 'MM/dd/yyyy')
          : '',
        __Annuitant_Address_1__: intakeState?.address || '',
        __Annuitant_Address_2__: intakeState?.address2 || '',
        __Annuitant_City__: intakeState?.city || '',
        __Annuitant_State__: selectData.states.find(
          (ac) => ac.value === (intakeState?.state || '')
        ).label,
        __Annuitant_State_Abbrev__: selectData.states.find(
          (ac) => ac.value === (intakeState?.state || '')
        ).abbr,
        __Annuitant_State_Number__: '',
        __Annuitant_Zipcode__: intakeState?.zipcode || '',
        __Annuitant_Phone__: intakeState?.phone || '',
        __Annuitant_Email__: intakeState?.email || '',
        __Client_Employment__: intakeState?.employmentStatus || 'Employed',
        __Owner_US_Citizen__: 'Yes',
        __Citizenship_Option__: 'Yes',
        __Country_Option__: 'United States',
        __Ownership_Type__: 'Individual',
        __Ownership_Type_Individual__: 'True',
        __Tax_Status__: 'Traditional IRA',
        __Owner_Begin_Using_Timeline__: '6-10 Years',
        __Owner_Is_Annuitant__: 'True',
        __Owner_Account_Type__: 'Qualified',
        __Owner_Account_Qualified_IRA__: 'true',
        __Owner_ID_Drivers__: 'true',
        __Owner_ID_Military__: 'false',
        __Owner_ID_Passport__: 'false',
        __Owner_ID_Type__: 'Drivers License',
        __Owner_ID_Country__: 'United States',
        __Owner_ID_State__: '',
        __Federal_Tax_Rate__: intakeState?.taxRate
          ? intakeState?.taxRate.toString()
          : '20',
        __Owner_Has_Reverse_Mortgage__: 'False',
        __Owner_Owns_Financial_Products__: 'False',
        __Power_Of_Attorney__: 'No',
        __Source_Of_Income__: 'Salary/Wages',
        __Num_Beneficiaries__: '1',
        __Beneficiary_Designation__: 'Primary',
        __Primary_Beneficiary__: '-',
        __Primary_Individual_Beneficiary__: 'True',
        __Beneficiary_Percentage__: '100',
        __Beneficiary_Firstname__: '',
        __Beneficiary_Lastname__: '',
        __Beneficiary_Relationship__: '',
        __Annuity_Premium__: localForm.amount.toString() || '',
        __Funding_Method_Code__: 'CHK',
        __Premium_Funding_Method_Personal__: 'false',
        __Premium_Funding_Method_Rollover__: 'true',
        __Premium_Funding_Method_Transfer__: 'false',
        __Premium_Contribution_Personal__: '0',
        __Premium_Contribution_Rollover__: '0',
        __Premium_Contribution_Transfer__: '0',
        __Fixed_Fund_Amount__: '60',
        __Annual_Amount__: '40',
        __Monthly_HH_Income__: '',
        __Monthly_HH_Expenses__: '',
        __Client_Stocks_Bonds__: '0',
        __Client_Annuity__: '0',
        __Client_Mutual_Funds__: '0',
        __Client_CD__: '0',
        __Client_Money_Market__: '0',
        __Client_Checking_Savings__: '0',
        __Client_Pension_401k__: '0',
        __Client_TSP__: '0',
        __Client_Net_Cash__: '0',
        __Client_Real_Estate__: '0',
        __Client_Annuities_Surrender_Period__: '0',
        __Client_Pension_Under_59__: '0',
        __Client_Limited_Partnership__: '0',
        __Client_Source_Checking_Savings__: 'true',
        __Client_Source_Stocks_Bonds__: 'false',
        __Client_Source_Other__: 'false',
        __Client_Source_CD__: 'false',
        __Client_Source_Fixed_Annuity__: 'false',
        __Client_Source_Fixed_Index_Annuity__: 'false',
        __Client_Source_Variable_Annuity__: 'false',
        __Client_Source_Life_Insurance__: 'false',
        __Hh_Liabilities__: '0',
        __Stocks_YOE__: '0',
        __Bonds_YOE__: '0',
        __Mutual_Funds_YOE__: '0',
        __Fixed_Annuities_YOE__: '0',
        __Variable_Annuities_YOE__: '0',
        __Life_Insurance_YOE__: '0',
        __Cds_YOE__: '0',
        __Pension_401k_YOE__: '0',
        __Dependents_HH__: 'No',
        __Address_Financial_Decision__: 'Yes',
        __Producer_Sold_Other_Contract__: 'No',
        __Apply_Gov_Benefits__: 'No',
        __Reverse_Mortgage__: 'No',
        __Client_Sufficient_Funds__: 'Yes',
        __Longterm_Annuity_Agreement__: 'Yes',
        __Longterm_Charge_Agreement__: 'No',
        __Longterm_Charge_Explanation__: 'Something here...',
        __Exchange_Disclosure__: 'No',
        __Relationship_Tax_Status__: '',
        __Client_Short_Term_Liabilities__: '0',
        __Client_Long_Term_Liabilities__: '0',
        __Yes_Existing_Annuity_App__: 'true',
        __No_Existing_Annuity_App__: 'false',
        __Yes_Replace_Annuity_App__: 'false',
        __No_Replace_Annuity_App__: 'true',
        __Existing_Annuity_App__: 'No',
        __Replace_Annuity_App__: 'No',
        __Existing_Annuity_Agent__: 'No',
        __Replace_Annuity_Agent__: 'No',
        __Sell_Fixed_Annuities__: 'true',
        __Sell_Variable_Annuities__: 'false',
        __Sell_Mutual_Funds__:
          intakeState?.retirementStrategist === 'john_mantia'
            ? 'true'
            : 'false',
        __Sell_Mutual_Funds_YN__:
          intakeState?.retirementStrategist === 'john_mantia' ? 'Yes' : '',
        '__Sell_Stocks/Bonds__':
          intakeState?.retirementStrategist === 'john_mantia'
            ? 'true'
            : 'false',
        '__Sell_Stocks/Bonds_YN__':
          intakeState?.retirementStrategist === 'john_mantia' ? 'Yes' : '',
        __Sell_Certificates_Of_Deposit__:
          intakeState?.retirementStrategist === 'john_mantia'
            ? 'true'
            : 'false',
        __Sell_Certificates_Of_Deposit_YN__:
          intakeState?.retirementStrategist === 'john_mantia' ? 'Yes' : '',
        __Sell_Life_Insurance__: 'true',
        __Sell_Annuities_From_One_Insurer__: 'false',
        __Sell_Annuities_From_Primary_Insurer__: 'false',
        __Sell_Annuities_From_More_Insurers__: 'true',
        __Paid_By_Commission__: 'true',
        __Paid_By_Commission_Description__:
          'I will be paid a commission for the recommendation of this annuity',
        '__Begin_Using_LPA_6-10_Years__': 'true',
        __PTE_Disclosure_Agreement__: 'true',
        __Risk_Tolerance__: 'Low',
        __Risk_Tolerance_Low__: 'true',
        __Risk_Tolerance_Moderate__: 'False',
        __Risk_Tolerance_High__: 'False',
        __Customer_First_Name__: intakeState?.firstName || '',
        __Customer_Last_Name__: intakeState?.lastName || '',
        __Applicant_First_Name__: intakeState?.firstName || '',
        __Applicant_Last_Name__: intakeState?.lastName || '',
        __Applicant_Full_Name__:
          intakeState?.firstName + ' ' + intakeState?.lastName || '',
        __Applicant_Extra_Address__: '',
        __Applicant_Extra_Address_City__: '',
        __Applicant_Extra_Address_State__: '',
        __Applicant_Extra_Address_Zipcode__: '',
        __Lincoln_Tax_Bracket_Less_13__:
          (intakeState?.taxRate || 20) < 13 ? 'true' : 'false',
        __Lincoln_Tax_Bracket_14_24__:
          (intakeState?.taxRate || 20) >= 13 &&
          (intakeState?.taxRate || 20) <= 24
            ? 'true'
            : 'false',
        __Lincoln_Tax_Bracket_More_24__:
          (intakeState?.taxRate || 20) > 24 ? 'true' : 'false',
      },
      annuitysetup: {
        __Carrier__: selectData.annuityCarriers.find(
          (ac) => ac.value === localForm.carrier
        ).label,
        __Product_Search__: selectData.annuityOptions.find(
          (ac) => ac.value === localForm.product
        ).label,
        __Applicant_Name__:
          intakeState?.firstName + ' ' + intakeState?.lastName || '',
        __Client_Date_Of_Birth__: intakeState?.dateOfBirth
          ? format(parseISO(intakeState?.dateOfBirth), 'MM/dd/yyyy')
          : '',
        __Client_Email__: intakeState?.email || '',
        __Client_Phone__: intakeState?.phone || '',
        __Client_Street_Address__: intakeState?.address || '',
        __Client_City__: intakeState?.city || '',
        __Client_State__: intakeState?.state || '',
        __Client_Zip__: intakeState?.zipcode || '',
        __Applicant_Gender__:
          selectData.gender.find((ac) => ac.value === intakeState?.gender)
            ?.label || '',
        __Primary_Rep_Name__:
          selectData.retirementStrategist.find(
            (ac) =>
              ac.value ===
              (intakeState?.retirementStrategist || 'john_mantia')
          ).label || '',
        __Primary_Rep_Commission_Option__: 'A',
        __Business_Source__: 'Current Client',
        __Primary_Rep_Commission_Percentage__:
          intakeState?.retirementStrategist === 'john_mantia' ? '100' : '1',
        __Split_Rep_Name__:
          intakeState?.retirementStrategist === 'john_mantia'
            ? ''
            : 'John Mantia',
        __Split_Rep_Commission_Option__: 'A',
        __Split_Rep_Commission_Percentage__:
          intakeState?.retirementStrategist === 'john_mantia' ? '0' : '99',
        __Product_Type__: 'Fixed Index',
        __Jurisdiction__: intakeState?.state
          ? selectData.states.find((ac) => ac.value === intakeState.state).label
          : '',
      },
    })

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow',
    }

    return requestOptions
  }

  const getJsonMapping = async () => {
    const carrierValue =
      selectData.annuityCarriers.find((ac) => ac.value === localForm?.carrier)
        .automationUrl || 'default'
    const requestOptions = getApplicationData()
    try {
      const response = await fetch(
        `https://signal-advisors-automation-440834939608.us-central1.run.app/api/map/${carrierValue}/json_mapping`,
        requestOptions
      )
      setJsonMapping(JSON.parse(await response.text())?.mapped_data)
    } catch (error) {
      console.error(error)
      toast.error('Error getting json mapping')
    }
  }

  const createAnnuityApplication = async () => {
    const requestOptions = getApplicationData()
    const carrierValue =
      selectData.annuityCarriers.find((ac) => ac.value === localForm?.carrier)
        .automationUrl || 'default'
    fetch(
      `https://signal-advisors-automation-440834939608.us-central1.run.app/api/map/${carrierValue}`,
      requestOptions
    )
      .then((response) => response.text())
      .then((result) => console.log(result))
      .catch((error) => console.error(error))

    toast.success(
      'You have successfully started the `Create Annuity Application` Automation',
      {
        position: 'top-center',
        autoClose: 5000,
        pauseOnFocusLoss: false,
        pauseOnHover: false,
      }
    )
  }

  const filteredCarriers = selectData.annuityCarriers.filter(
    (carrier) => carrier.isMapped
  )
  const filteredProducts = selectData.annuityOptions.filter(
    (option) => option.provider === localForm?.carrier
    // (option) => option.provider === localForm?.carrier && option.isMapped
  )
  return (
    <>
      <Grid item xs={12} md={4} xl={3}>
        {/* <Card
            sx={{
              overflow: 'visible',
              textAlign: 'left',
              boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
              backgroundColor: '#f8f9fa',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Grid container columnSpacing={3} rowSpacing={2}>
              <Grid item xs={12}>
                <Typography variant="valueTitle">VALUE POINTS</Typography>
                <Collapse
                  in={collapseSections.annuityapplicationcreator}
                  style={{ width: '100%' }}
                ></Collapse>
              </Grid>
            </Grid>
          </Card> */}
      </Grid>
      <Grid item xs={12} md={8} xl={9} sx={{ pb: 0 }}>
        <Card
          id="annuityapplicationcreator"
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            borderRadius: 2,
            p: 2,
            pb: 0,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
            <Grid
              item
              xs={12}
              onClick={() => toggleCollapse('annuityapplicationcreator')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                Annuity Application Creator
              </Typography>
              <ExpandMore
                sx={{
                  height: '1.5em',
                  width: '1.5em',
                  transform: collapseSections.annuityapplicationcreator
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Grid>
          </Grid>
          <Collapse
            in={collapseSections.annuityapplicationcreator}
            style={{ width: '100%' }}
          >
            <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
              <Grid item xs={12} sm={4}>
                {renderMuiFormField({
                  autoFocus: false,
                  label: 'Carrier',
                  placeholder: 'Select',
                  inputType: 'select',
                  value: localForm.carrier,
                  onChange: (e) => handleChange('carrier')(e),
                  onBlur: onBlur,
                  options: filteredCarriers,
                })}
              </Grid>
              <Grid item xs={12} sm={4}>
                {renderMuiFormField({
                  autoFocus: false,
                  label: 'Product',
                  placeholder: 'Select',
                  inputType: 'select',
                  value: localForm.product,
                  onChange: (e) => handleChange('product')(e),
                  onBlur: onBlur,
                  options: filteredProducts,
                })}
              </Grid>
              <Grid item xs={12} sm={4}>
                {renderMuiFormField({
                  label: 'Annuity Amount',
                  shrink: true,
                  placeholder: '$150,000.00',
                  inputType: 'currencyFloat',
                  value: localForm.amount,
                  onChange: (e) => handleChange('amount')(e),
                  onBlur: onBlur,
                })}
              </Grid>
              <Grid item xs={12} display="flex" gap={2}>
                <Button
                  variant="parcoRounded"
                  onClick={createAnnuityApplication}
                  disabled={!localForm.carrier || !localForm.product}
                >
                  Create Annuity Application
                </Button>
                <Button
                  variant="parcoRounded"
                  onClick={getJsonMapping}
                  disabled={!localForm.carrier || !localForm.product}
                >
                  Get Application Data
                </Button>
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(jsonMapping)
                    setSnackbarOpen(true)
                  }}
                  aria-label="Copy json mapping to clipboard"
                  disabled={!jsonMapping}
                  sx={{
                    ml: 1,
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    '&:hover': {
                      backgroundColor: '#efefef',
                    },
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
                <Snackbar
                  open={snackbarOpen}
                  autoHideDuration={2000}
                  onClose={() => setSnackbarOpen(false)}
                  message="Json mapping copied to clipboard"
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Grid>
    </>
  )
}

export default AnnuityApplicationCreator
